import { APP_LANGUAGES } from "@/types";

const ALL: string[] = APP_LANGUAGES.map((l) => l.code);

export interface CustomLanguage {
  code: string;
  name: string;
}

interface LangSettings {
  languages?: string[];
  custom_languages?: CustomLanguage[];
}

/**
 * Languages the tour offers translated names in (tour title, hotspots, rooms,
 * floors). The primary language is always included and listed first. Unset
 * `settings.languages` = all app languages. Owner-defined custom languages
 * (Pro) are allowed alongside the app ones.
 */
export function tourLanguages(
  settings: LangSettings | null | undefined,
  primary: string,
): string[] {
  const custom = settings?.custom_languages?.map((c) => c.code) ?? [];
  const allowed = new Set([...ALL, ...custom]);
  const set = settings?.languages?.length ? settings.languages : ALL;
  return [primary, ...set.filter((c) => c !== primary && allowed.has(c))];
}

/** Display name for a language code: app languages via i18n, custom via its
 *  stored name (falls back to the uppercased code). */
export function languageLabel(
  code: string,
  custom: CustomLanguage[] | null | undefined,
  t: (key: string) => string,
): string {
  if (ALL.includes(code)) return t(`lang_${code}`);
  return custom?.find((c) => c.code === code)?.name ?? code.toUpperCase();
}

/** True for a code that isn't one of the 5 app languages. */
export function isCustomLanguage(code: string): boolean {
  return !ALL.includes(code);
}

/** Map of custom language code → display name (for `labelFor` props). */
export function customLanguageNames(
  settings: LangSettings | null | undefined,
): Record<string, string> {
  return Object.fromEntries(
    (settings?.custom_languages ?? []).map((c) => [c.code, c.name]),
  );
}
